// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

import { useCallback } from 'react';
import { defineMessages } from 'react-intl-next';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment$data } from './__generated__/useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment.graphql.ts';
import type {
	useSpaceViewsOptionsSpaceViewsPersistenceMutation$data,
	useSpaceViewsOptionsSpaceViewsPersistenceMutation$variables,
} from './__generated__/useSpaceViewsOptionsSpaceViewsPersistenceMutation.graphql.ts';
import type {
	useSpaceViewsOptionsPersistenceQuery$data,
	useSpaceViewsOptionsPersistenceQuery$variables,
	SpaceViewsPersistenceOption,
} from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';

const useSpaceViewsOptionsSpaceViewsPersistenceMutation = graphql`
	mutation useSpaceViewsOptionsSpaceViewsPersistenceMutation(
		$spaceKey: String!
		$persistenceOption: SpaceViewsPersistenceOption!
	) {
		updateUserPreferences(
			userPreferences: {
				spaceViewsPersistence: { spaceKey: $spaceKey, persistenceOption: $persistenceOption }
			}
		) {
			spaceViewsPersistence(spaceKey: $spaceKey)
		}
	}
`;

const useSpaceViewsOptionsPersistenceQuery = graphql`
	query useSpaceViewsOptionsPersistenceQuery($spaceKey: String!) {
		userPreferences {
			spaceViewsPersistence(spaceKey: $spaceKey)
		}
	}
`;

export const sortI18n = defineMessages({
	TREE: {
		id: 'space-views.tree',
		defaultMessage: 'Tree',
		description: 'dropdown menu value for the default tree view',
	},
	RECENTLY_VIEWED: {
		id: 'space-views.recently.viewed',
		defaultMessage: 'My visits',
		description:
			'dropdown menu value for filtering by only pages the current viewer has recently viewed',
	},
	RECENTLY_MODIFIED: {
		id: 'space-views.recently.modified.nonfinal', // Remove .nonfinal suffix when copy is finalized
		defaultMessage: 'Last updated',
		description:
			'dropdown menu value for sorting by pages in the order that they were most recently modified',
	},
	TITLE_AZ: {
		id: 'space-views.a-z.desc',
		defaultMessage: 'Title - A to Z',
		description: 'dropdown menu value for sorting by title from A-Z',
	},
});

export const useSpaceViewsOptions = (spaceKey, isLicensed = false) => {
	const {
		data,
		loading: queryLoading,
		error: queryError,
	} = useQueryInterop<
		useSpaceViewsOptionsPersistenceQuery$data,
		useSpaceViewsOptionsPersistenceQuery$variables
	>(useSpaceViewsOptionsPersistenceQuery, {
		variables: { spaceKey },
		skip: !isLicensed,
	});

	const [updatePersistence, { loading: mutationLoading, error: mutationError }] =
		useMutationInterop<
			useSpaceViewsOptionsSpaceViewsPersistenceMutation$data,
			useSpaceViewsOptionsSpaceViewsPersistenceMutation$variables
		>(useSpaceViewsOptionsSpaceViewsPersistenceMutation);

	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: graphql`
			fragment useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment on UserPreferences {
				spaceViewsPersistence(spaceKey: $spaceKey)
				__typename
			}
		`,
	});
	const setSpaceViewsSort = useCallback(
		(newSort: SpaceViewsPersistenceOption) => {
			updatePersistence({
				variables: {
					spaceKey,
					persistenceOption: newSort,
				},
			}).catch(() => {}); // ignoring errors here because we are consuming error from useMutation

			// Update the cache to keep UI updated regardless of whether the mutation succeeds
			writeFragmentInterop({
				fragmentId: `$ROOT_QUERY.userPreferences`,
				data: {
					spaceViewsPersistence: newSort,
					__typename: 'UserPreferences',
				},
				variables: { spaceKey },
			});
		},
		[spaceKey, updatePersistence, writeFragmentInterop],
	);

	const spaceViewsSort: SpaceViewsPersistenceOption =
		data?.userPreferences?.spaceViewsPersistence || 'TREE'; //tree is default

	return {
		spaceViewsSort,
		setSpaceViewsSort,
		queryLoading,
		queryError,
		mutationLoading,
		mutationError,
	};
};
